import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Typography,
  Box,
} from '@mui/material';
import { CheckCircle as CheckCircleIcon } from '@mui/icons-material';

/**
 * CompletionDialog Component
 *
 * Displays when the survey has been successfully completed and submitted.
 * Provides confirmation to the user and navigation back to the home page.
 *
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {string} props.type - Type of completion ('submitted' or 'saved')
 * @param {Function} props.onClose - Callback function when dialog is closed
 */
const CompletionDialog = ({ open, type, onClose }) => {
  /**
   * Get the appropriate title based on completion type
   */
  const getTitle = () => {
    switch (type) {
      case 'submitted':
        return 'Survey Submitted Successfully';
      case 'saved':
        return 'Survey Saved Successfully';
      default:
        return 'Action Completed';
    }
  };

  /**
   * Get the appropriate message based on completion type
   */
  const getMessage = () => {
    switch (type) {
      case 'submitted':
        return {
          primary:
            'Your facilities walkthrough has been successfully submitted and processed.',
          secondary:
            'The survey results have been recorded and will be used for facility management and improvement planning. Any issues identified will be forwarded to the appropriate teams for resolution.',
        };
      case 'saved':
        return {
          primary: 'Your survey progress has been saved successfully.',
          secondary:
            'You can return to complete the survey at any time. Your progress will be automatically restored when you return.',
        };
      default:
        return {
          primary: 'The operation has been completed successfully.',
          secondary: 'You can now proceed with other tasks.',
        };
    }
  };

  const { primary, secondary } = getMessage();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby='completion-dialog-title'
      aria-describedby='completion-dialog-description'
      maxWidth='sm'
      fullWidth
      sx={{
        '& .MuiBackdrop-root': {
          height: '150vh',
        },
      }}
    >
      <DialogTitle id='completion-dialog-title'>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <CheckCircleIcon color='success' sx={{ fontSize: 32 }} />
          <Typography
            variant='h6'
            component='span'
            style={{ fontFamily: 'Lato' }}
          >
            {getTitle()}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <DialogContentText id='completion-dialog-description'>
          <Typography variant='body1' paragraph style={{ fontFamily: 'Lato' }}>
            {primary}
          </Typography>

          <Typography
            variant='body2'
            color='textSecondary'
            style={{ fontFamily: 'Lato' }}
          >
            {secondary}
          </Typography>
        </DialogContentText>

        {/* Additional info for submitted surveys
        {type === 'submitted' && (
          <Box 
            sx={{ 
              mt: 3, 
              p: 2, 
              backgroundColor: 'success.50',
              border: 1,
              borderColor: 'success.main',
              borderRadius: 1
            }}
          >
            <Typography variant="subtitle2" color="success.main" gutterBottom>
              What happens next?
            </Typography>
            <Typography variant="body2" color="textSecondary">
              • Survey data has been integrated with facility management systems<br />
              • Identified issues have been logged for remediation<br />
              • Responsible teams will be notified of action items<br />
              • Progress on improvements will be tracked and reported
            </Typography>
          </Box>
        )} */}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={onClose}
          variant='contained'
          color='primary'
          size='large'
          fullWidth
        >
          Return to Home
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CompletionDialog;
